import { useState, useEffect } from "react";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { 
  Menu, X, ChevronDown, UserCircle, LogOut, 
  LayoutDashboard, FileText, Palette, Shield, Home as HomeIcon
} from "lucide-react";
// Logo is in public folder for LCP discoverability - using PNG for proper transparency
const byrdsonLogo = "/byrdson-logo.png";
import { useLanguage } from "@/contexts/LanguageContext";

export const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();
  const isHomePage = location.pathname === "/";
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [displayName, setDisplayName] = useState<string>("");
  const [isAdmin, setIsAdmin] = useState(false);

  const toggleLanguage = () => {
    setLanguage(language === "EN" ? "ES" : "EN");
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          setTimeout(() => {
            fetchProfile(session.user.id);
            checkAdminStatus(session.user.id);
          }, 0);
        } else {
          setDisplayName("");
          setIsAdmin(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
        checkAdminStatus(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string) => {
    const { data } = await supabase
      .from('profiles')
      .select('display_name')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (data?.display_name) {
      setDisplayName(data.display_name);
    }
  };

  const checkAdminStatus = async (userId: string) => {
    const { data } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .eq('role', 'admin')
      .maybeSingle();
    
    setIsAdmin(!!data);
  };

  const handleSignOut = async () => {
    try {
      // Clear local state first
      setUser(null);
      setSession(null);
      setDisplayName("");
      setIsAdmin(false);
      
      // Attempt to sign out from Supabase
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      // Even if sign out fails on server, clear local session
      console.log("Sign out completed");
    } finally {
      // Always navigate to home
      navigate("/");
      setMobileMenuOpen(false);
    }
  };

  const getInitials = (name: string) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
      isActive
        ? "bg-secondary text-secondary-foreground"
        : "text-foreground/70 hover:text-foreground hover:bg-muted"
    }`;

  // Don't show nav links on home page (except for logged-in users)
  const showNavLinks = !isHomePage || user;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 sm:h-18">
          {/* Logo */}
          <div 
            className="flex items-center cursor-pointer group shrink-0" 
            onClick={() => navigate("/")}
          >
            <div className="flex items-center gap-2.5">
              <img 
                src={byrdsonLogo} 
                alt="Byrdson Services Logo"
                // @ts-ignore - fetchpriority is valid HTML attribute
                fetchpriority="high"
                decoding="sync"
                width={56}
                height={48}
                className="w-12 h-10 sm:w-14 sm:h-12 object-contain transition-transform duration-300 group-hover:scale-105"
              />
              <div className="flex flex-col transition-transform duration-300 group-hover:scale-105">
                <span className="text-lg sm:text-xl font-bold tracking-wide text-secondary">
                  BYRDSON
                </span>
                <span className="text-xs sm:text-sm font-medium text-foreground/70 -mt-0.5">
                  Services
                </span>
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          {showNavLinks && (
            <nav className="hidden lg:flex items-center gap-1">
              {!isHomePage && (
                <NavLink to="/" end className={navLinkClass}>
                  <span className="flex items-center gap-2">
                    <HomeIcon className="w-4 h-4" />
                    {t('header.home')}
                  </span>
                </NavLink>
              )}
              <NavLink to="/choose-models" className={navLinkClass}>
                <span className="flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  {t('header.chooseModels')}
                </span>
              </NavLink>
              <NavLink to="/track-status" className={navLinkClass}>
                <span className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  {language === "EN" ? "Project Estimate" : "Presupuesto"}
                </span>
              </NavLink>
              {user && (
                <NavLink to="/dashboard" className={navLinkClass}>
                  <span className="flex items-center gap-2">
                    <LayoutDashboard className="w-4 h-4" />
                    {t('header.myDesigns')}
                  </span>
                </NavLink>
              )}
              {isAdmin && (
                <NavLink to="/admin" className={navLinkClass}>
                  <span className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Admin
                  </span>
                </NavLink>
              )}
            </nav>
          )}

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="font-medium text-foreground/70 hover:text-foreground hover:bg-muted transition-all duration-200 px-3"
            >
              {language === "EN" ? "EN" : "ES"} | {language === "EN" ? "ES" : "EN"}
            </Button>

            {user ? (
              /* User Dropdown */
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 px-2 py-1.5 h-auto hover:bg-muted transition-all duration-200"
                  >
                    <Avatar className="w-8 h-8 border-2 border-secondary/20">
                      <AvatarFallback className="bg-secondary text-secondary-foreground text-sm font-semibold">
                        {getInitials(displayName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col items-start text-left hidden sm:flex">
                      <span className="text-sm font-semibold text-foreground leading-tight">
                        {displayName || "User"}
                      </span>
                      <span className="text-xs text-foreground/50 leading-tight">
                        {isAdmin ? "Administrator" : "Member"}
                      </span>
                    </div>
                    <ChevronDown className="w-4 h-4 text-foreground/50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end" 
                  className="w-56 bg-card border border-border shadow-lg z-50 p-2"
                  sideOffset={8}
                >
                  <DropdownMenuLabel className="px-2 py-2">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-10 h-10 border-2 border-secondary/20">
                        <AvatarFallback className="bg-secondary text-secondary-foreground font-semibold">
                          {getInitials(displayName)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="font-semibold text-foreground">
                          {displayName || "User"}
                        </span>
                        <span className="text-xs text-foreground/50">
                          {user.email}
                        </span>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="my-2" />
                  
                  <DropdownMenuItem 
                    onClick={() => navigate("/profile")}
                    className="cursor-pointer px-3 py-2.5 rounded-lg text-sm font-medium"
                  >
                    <UserCircle className="w-4 h-4 mr-3 text-foreground/60" />
                    {language === "EN" ? "My Profile" : "Mi Perfil"}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate("/dashboard")}
                    className="cursor-pointer px-3 py-2.5 rounded-lg text-sm font-medium"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-3 text-foreground/60" />
                    {t('header.myDesigns')}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate("/track-status")}
                    className="cursor-pointer px-3 py-2.5 rounded-lg text-sm font-medium"
                  >
                    <FileText className="w-4 h-4 mr-3 text-foreground/60" />
                    {language === "EN" ? "My Estimates" : "Mis Estimaciones"}
                  </DropdownMenuItem>
                  
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator className="my-2" />
                      <DropdownMenuItem 
                        onClick={() => navigate("/admin")}
                        className="cursor-pointer px-3 py-2.5 rounded-lg text-sm font-medium"
                      >
                        <Shield className="w-4 h-4 mr-3 text-foreground/60" />
                        Admin Dashboard
                      </DropdownMenuItem>
                    </>
                  )}
                  
                  <DropdownMenuSeparator className="my-2" />
                  <DropdownMenuItem 
                    onClick={handleSignOut}
                    className="cursor-pointer px-3 py-2.5 rounded-lg text-sm font-medium text-destructive focus:text-destructive focus:bg-destructive/10"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    {t('header.signOut')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              /* Sign In/Up Buttons */
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/auth")}
                  className="font-medium text-foreground/70 hover:text-foreground hover:bg-muted transition-all duration-200"
                >
                  {t('header.signIn')}
                </Button>
                <Button
                  size="sm"
                  onClick={() => navigate("/auth")}
                  className="font-semibold bg-secondary text-secondary-foreground hover:bg-secondary/90 transition-all duration-200 px-5"
                >
                  {t('header.signUp')}
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden p-2 text-foreground hover:bg-muted"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border animate-fade-in">
            {/* User Info (if logged in) */}
            {user && (
              <div className="flex items-center gap-3 px-2 py-3 mb-3 bg-muted/50 rounded-lg">
                <Avatar className="w-10 h-10 border-2 border-secondary/20">
                  <AvatarFallback className="bg-secondary text-secondary-foreground font-semibold">
                    {getInitials(displayName)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <span className="font-semibold text-foreground">
                    {displayName || "User"}
                  </span>
                  <span className="text-xs text-foreground/50">
                    {isAdmin ? "Administrator" : "Member"}
                  </span>
                </div>
              </div>
            )}
            
            {/* Navigation Links */}
            <nav className="space-y-1 mb-4">
              {!isHomePage && (
                <NavLink
                  to="/"
                  end
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                      isActive 
                        ? "bg-secondary text-secondary-foreground" 
                        : "text-foreground/70 hover:text-foreground hover:bg-muted"
                    }`
                  }
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <HomeIcon className="w-5 h-5" />
                  {t('header.home')}
                </NavLink>
              )}
              <NavLink
                to="/choose-models"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                    isActive 
                      ? "bg-secondary text-secondary-foreground" 
                      : "text-foreground/70 hover:text-foreground hover:bg-muted"
                  }`
                }
                onClick={() => setMobileMenuOpen(false)}
              >
                <Palette className="w-5 h-5" />
                {t('header.chooseModels')}
              </NavLink>
              <NavLink
                to="/track-status"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                    isActive 
                      ? "bg-secondary text-secondary-foreground" 
                      : "text-foreground/70 hover:text-foreground hover:bg-muted"
                  }`
                }
                onClick={() => setMobileMenuOpen(false)}
              >
                <FileText className="w-5 h-5" />
                {language === "EN" ? "Project Estimate" : "Presupuesto"}
              </NavLink>
              {user && (
                <>
                  <NavLink
                    to="/dashboard"
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                        isActive 
                          ? "bg-secondary text-secondary-foreground" 
                          : "text-foreground/70 hover:text-foreground hover:bg-muted"
                      }`
                    }
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <LayoutDashboard className="w-5 h-5" />
                    {t('header.myDesigns')}
                  </NavLink>
                  <NavLink
                    to="/profile"
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                        isActive 
                          ? "bg-secondary text-secondary-foreground" 
                          : "text-foreground/70 hover:text-foreground hover:bg-muted"
                      }`
                    }
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <UserCircle className="w-5 h-5" />
                    {language === "EN" ? "Profile" : "Perfil"}
                  </NavLink>
                  {isAdmin && (
                    <NavLink
                      to="/admin"
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                          isActive 
                            ? "bg-secondary text-secondary-foreground" 
                            : "text-foreground/70 hover:text-foreground hover:bg-muted"
                        }`
                      }
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Shield className="w-5 h-5" />
                      Admin
                    </NavLink>
                  )}
                </>
              )}
            </nav>

            {/* Actions */}
            <div className="pt-4 border-t border-border space-y-3">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleLanguage}
                className="w-full font-medium transition-all duration-200"
              >
                {language === "EN" ? "Switch to Spanish" : "Cambiar a Inglés"}
              </Button>

              {user ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    handleSignOut();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full font-medium text-destructive border-destructive/30 hover:bg-destructive/10 hover:border-destructive transition-all duration-200"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('header.signOut')}
                </Button>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigate("/auth");
                      setMobileMenuOpen(false);
                    }}
                    className="w-full font-medium transition-all duration-200"
                  >
                    {t('header.signIn')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      navigate("/auth");
                      setMobileMenuOpen(false);
                    }}
                    className="w-full font-semibold bg-secondary text-secondary-foreground hover:bg-secondary/90 transition-all duration-200"
                  >
                    {t('header.signUp')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
